import type { ColumnsType, ColumnType, SortOrder } from "antd/es/table/interface";
import type { SorterResult } from "antd/es/table/interface";

import type { TableSortState } from "./types";

/** 解析列排序字段：仅取 dataIndex */
export const resolveColumnKey = (column: ColumnType<unknown>): string | undefined => {
  const { dataIndex } = column;
  if (typeof dataIndex === "string") return dataIndex;
  if (Array.isArray(dataIndex)) return dataIndex.join(".");
  return undefined;
};

/** 判断列是否配置了 dataIndex */
const hasColumnDataIndex = (column: ColumnType<unknown>): boolean =>
  resolveColumnKey(column) != null;

/** 判断列是否可排序：有 dataIndex 默认启用，显式 sorter: false 则禁用 */
export const isColumnSortable = (column: ColumnType<unknown>): boolean => {
  if (column.sorter === false) return false;
  return hasColumnDataIndex(column);
};

/** 判断列配置中是否存在可排序列 */
export const hasSortableColumn = <T extends object>(columns: ColumnsType<T>): boolean => {
  for (const col of columns) {
    if ("children" in col && col.children?.length) {
      if (hasSortableColumn(col.children)) return true;
      continue;
    }
    if (isColumnSortable(col as ColumnType<unknown>)) return true;
  }
  return false;
};

/** 为可排序列补全 sorter 并注入受控 sortOrder，驱动列头图标状态 */
export const injectSortOrder = <T extends object>(
  columns: ColumnsType<T>,
  sortState: TableSortState | null,
): ColumnsType<T> =>
  columns.map((col) => {
    if ("children" in col && col.children) {
      return { ...col, children: injectSortOrder(col.children, sortState) };
    }
    const column = col as ColumnType<T>;
    if (!isColumnSortable(column as ColumnType<unknown>)) return col;
    const key = resolveColumnKey(column as ColumnType<unknown>);
    return {
      ...column,
      sorter: column.sorter ?? true,
      sortOrder: key && sortState?.field === key ? sortState.order : undefined,
    };
  });

/** 从 antd onChange sorter 解析排序字段 */
export const resolveSorterField = <T>(
  sorter: SorterResult<T> | SorterResult<T>[],
): { field: string; order: SortOrder } | null => {
  /** 排序结果不存在，则返回 null */
  if (Array.isArray(sorter) || !sorter.order) return null;
  /** 排序结果存在，则返回 dataIndex 对应字段与排序方向 */
  const field = sorter.field;
  if (field == null) return null;
  const normalized = Array.isArray(field) ? field.join(".") : String(field);
  return { field: normalized, order: sorter.order };
};

/** 按 dataIndex 路径读取行字段值，支持 a.b.c 嵌套；未取到则 undefined */
const getFieldValue = (record: object, field: string): unknown => {
  const keys = field.split(".");
  let current: unknown = record;
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
};

/** 判断排序时视为「空值」 */
const isEmptySortValue = (value: unknown): value is null | undefined =>
  value === null || value === undefined;

/** 默认字段比较（升序） */
const defaultFieldCompare = (a: unknown, b: unknown): number => {
  const aEmpty = isEmptySortValue(a);
  const bEmpty = isEmptySortValue(b);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return -1;
  if (bEmpty) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  /** 字符串比较，中文排序 */
  return String(a).localeCompare(String(b), "zh-CN");
};

/** 构造指定字段的升序比较器 */
export const buildFieldCompare = <T extends object>(
  field: string,
  sortComparators?: Partial<Record<string, (a: T, b: T) => number>>,
): ((a: T, b: T) => number) => {
  /** 自定义比较器 */
  const custom = sortComparators?.[field];
  /** 自定义比较器存在，则使用自定义比较器 */
  if (custom) return custom;
  /** 默认比较器 */
  return (a, b) => defaultFieldCompare(getFieldValue(a, field), getFieldValue(b, field));
};

/** 扁平 dataSource 本地排序 */
export const sortFlatData = <T extends object>(
  data: readonly T[],
  sortState: TableSortState,
  sortComparators?: Partial<Record<string, (a: T, b: T) => number>>,
): T[] => {
  /** 构造指定字段的升序比较器 */
  const compare = buildFieldCompare<T>(sortState.field, sortComparators);
  /** 排序因子：升序 1，降序 -1 */
  const factor = sortState.order === "ascend" ? 1 : -1;
  return [...data].sort((a, b) => compare(a, b) * factor);
};

/** 树形 dataSource：递归排序每层兄弟节点 */
export const sortTreeData = <T extends { children?: readonly T[] }>(
  data: readonly T[],
  sortState: TableSortState,
  sortComparators?: Partial<Record<string, (a: T, b: T) => number>>,
): T[] => {
  /** 构造指定字段的升序比较器 */
  const compare = buildFieldCompare<T>(sortState.field, sortComparators);
  /** 排序因子：升序 1，降序 -1 */
  const factor = sortState.order === "ascend" ? 1 : -1;
  return [...data]
    .sort((a, b) => compare(a, b) * factor)
    .map((node) => ({
      ...node,
      children: node.children?.length
        ? sortTreeData(node.children, sortState, sortComparators)
        : node.children,
    }));
};
